// src/client/main.js
import { createApp, reactive } from "vue";
import App from "./App.vue";
import router from "./router";
import { useAuth } from "@/composables/useAuth.js";
import { useModalOpeners } from "@/composables/useModalOpeners.js";
import { initIdleTimer } from "@/utils/idleTimer.js";
// --------------------------------------------------
// AG Grid Setup
// --------------------------------------------------
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ModuleRegistry } from "ag-grid-community";
import {
  ClientSideRowModelModule,
  InfiniteRowModelModule,
  PaginationModule,
  ValidationModule,
  ColumnAutoSizeModule,
  TextEditorModule,
  SelectEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CheckboxEditorModule,
  TextFilterModule,
  NumberFilterModule,
  RenderApiModule,
  DateFilterModule,
  EventApiModule,
  CustomEditorModule,
  CellStyleModule,
  ScrollApiModule,
  PinnedRowModule
} from "ag-grid-community";

import "ag-grid-enterprise";
import "./styles/main.less";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  InfiniteRowModelModule,
  PaginationModule,
  ValidationModule,
  ColumnAutoSizeModule,
  TextEditorModule,
  SelectEditorModule,
  NumberEditorModule,
  DateEditorModule,
  CheckboxEditorModule,
  TextFilterModule,
  NumberFilterModule,
  RenderApiModule,
  DateFilterModule,
  EventApiModule,
  CustomEditorModule,
  CellStyleModule,
  ScrollApiModule,
  PinnedRowModule
]);

// --------------------------------------------------
// Create Vue App
// --------------------------------------------------
const app = createApp(App);
app.use(router);

// --------------------------------------------------
// Global Model Store → used for dynamic body classes
// --------------------------------------------------
import { useModelStore } from "@/composables/useModelStore.js";
const { currentModel } = useModelStore();

app.config.globalProperties.$nzState = reactive({
  currentModel,
});

// --------------------------------------------------
// Dynamic BODY classes per route
// --------------------------------------------------
router.afterEach((to) => {
  document.body.className = "";

  const routeClass = `page-${to.name || to.path.replace("/", "")}`;
  document.body.classList.add(routeClass);

  if (currentModel.value) {
    document.body.classList.add(`model-${currentModel.value}`);
  }
});

// --------------------------------------------------
// Mount the App
// --------------------------------------------------
app.mount("#app");

// --------------------------------------------------
// Authentication Restore (CRITICAL)
// --------------------------------------------------
const auth = useAuth();

// 1) Restore token from localStorage if missing
const savedToken = localStorage.getItem("access_token");
if (savedToken && !auth.accessToken.value) {
  auth.accessToken.value = savedToken;
}

// 2) If token exists but user not loaded → load user once
(async () => {
  if (auth.accessToken.value && !auth.user.value) {
    await auth.fetchUser(); // this safely starts idle timer
  }
})();

// --------------------------------------------------
// Global modal opener factory
// (AG-Grid & vanilla DOM call this)
// --------------------------------------------------
window.$modalOpeners = () => useModalOpeners();

// --------------------------------------------------
// Idle Timer: attaches global activity listeners
// Timer does NOT run yet — starts inside fetchUser()
// --------------------------------------------------
initIdleTimer(
  async () => {
    console.log("[idleTimer] Auto-logout triggered.");
    await auth.logout();
  },
  {
    timeoutMs: 1 * 60 * 1000, // 1 min default for listeners only (not used)
    heartbeatMs: 1000,
  }
);
